import { useEffect, useState } from "react";
import { UploadIcon, DownloadIcon } from "@heroicons/react/outline";
import { exportClients, getClients } from "../../api/clients";
import PageLoader from "../../components/pageLoader";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import { getEvents } from "../../api/eventsApi";
import ImportClientsModal from "./ImportClientsModal";

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [events, setEvents] = useState([]);

  const [showImportModal, setShowImportModal] = useState(false);

  const [limit, setLimit] = useState(10);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / limit);

  const getPageNumbers = () => {
    const delta = 2; // pages to show on each side of current page
    const range = [];

    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    range.push(1); // always show first page

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) range.push(i);

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages); // always show last page

    return range;
  };

  const [filters, setFilters] = useState({
    event: "All",
    ticketsRange: "All",
    vip: "All",
    table: "All",
  });

  const yesNoOptions = [
    { label: "All", value: "All" },
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const ticketOptions = [
    { label: "All", value: "All" },
    { label: "0-10", value: "0-10" },
    { label: "11-20", value: "11-20" },
    { label: "20+", value: "20+" },
  ];

  const fetchClients = async () => {
    try {
      setLoading(true);

      const offset = (page - 1) * limit;

      const data = await getClients(limit, offset, filters);

      setClients(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      const list = Array.isArray(data) ? data : data.results || [];

      const eventOptions = [
        { label: "All", value: "All" },
        ...list.map((ev) => ({
          label: ev.name,
          value: ev.id,
        })),
      ];

      setEvents(eventOptions);
    } catch (err) {
      // handle later if needed
    }
  };

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  useEffect(() => {
    fetchClients();
  }, [page, filters, limit]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    fetchEvents();
  }, []);

  // const filteredClients = clients.filter((client) => {
  //   // EVENT
  //   if (
  //     filters.event &&
  //     filters.event !== "All" &&
  //     client.last_event_attended !== filters.event
  //   ) {
  //     return false;
  //   }

  //   // TICKETS RANGE
  //   if (filters.ticketsRange && filters.ticketsRange !== "All") {
  //     const tickets = client.tickets_purchased || 0;

  //     if (filters.ticketsRange === "Any" && tickets <= 0) {
  //       return false;
  //     }

  //     if (filters.ticketsRange === "0-10" && !(tickets >= 0 && tickets <= 10)) {
  //       return false;
  //     }

  //     if (
  //       filters.ticketsRange === "11-20" &&
  //       !(tickets >= 11 && tickets <= 20)
  //     ) {
  //       return false;
  //     }

  //     if (filters.ticketsRange === "20+" && !(tickets > 20)) {
  //       return false;
  //     }
  //   }

  //   // VIP / BACKSTAGE
  //   if (filters.vip && filters.vip !== "All") {
  //     const isVip = client.is_vip || client.is_backstage;

  //     if (filters.vip === "Yes" && !isVip) return false;
  //     if (filters.vip === "No" && isVip) return false;
  //   }

  //   // TABLE
  //   if (filters.table && filters.table !== "All") {
  //     if (filters.table === "Yes" && !client.is_table) return false;
  //     if (filters.table === "No" && client.is_table) return false;
  //   }

  //   return true;
  // });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const handleExport = async () => {
    try {
      const blob = await exportClients();

      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;

      const fileName = `clients_${new Date().toISOString().slice(0, 10)}.csv`;

      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to export clients",
      });
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-semibold">Clients</h1>

        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-slate-800 border border-white/10 rounded-lg text-white hover:bg-slate-700"
          >
            <UploadIcon className="w-4 h-4" />
            Import CSV
          </button>

          <button
            onClick={handleExport}
            disabled={!clients.length}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-pink-600 rounded-lg text-white hover:bg-pink-700"
          >
            <DownloadIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select
          label="Event"
          value={filters.event}
          onChange={(v) => setFilters({ ...filters, event: v })}
          options={events}
        />

        <Select
          label="Tickets Purchased"
          value={filters.ticketsRange}
          onChange={(v) => setFilters({ ...filters, ticketsRange: v })}
          options={ticketOptions}
        />

        <Select
          label="VIP / Backstage"
          value={filters.vip}
          onChange={(v) => setFilters({ ...filters, vip: v })}
          options={yesNoOptions}
        />

        <Select
          label="Table Customer"
          value={filters.table}
          onChange={(v) => setFilters({ ...filters, table: v })}
          options={yesNoOptions}
        />
      </div>

      {/* TABLE */}
      <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-center border-white/10 text-xs uppercase tracking-wider text-slate-400">
              <th className="py-2">First Name</th>
              <th className="py-2">Last Name</th>
              <th className="py-2">Email</th>
              <th className="py-2">Phone No</th>
              <th className="py-2">Last Event Attended</th>
              <th className="py-2">Tickets Purchased</th>
              <th className="py-2">Mobile App</th>
              <th className="py-2">Promo Consent</th>
              <th className="py-2">Created At</th>
            </tr>
          </thead>

          <tbody>
            {clients.length > 0 ? (
              clients.map((client) => (
                <tr
                  key={client.id}
                  className="border-b border-white/5 hover:bg-slate-800/40"
                >
                  <td className="py-4 text-center text-white">
                    {client.first_name}
                  </td>
                  <td className="py-4 text-center text-white">
                    {client.last_name}
                  </td>
                  <td className="py-4 text-center text-slate-400">
                    {client.email}
                  </td>
                  <td className="py-4 text-center text-slate-400">
                    {client.phone_no}
                  </td>
                  <td className="py-4 text-center text-slate-400">
                    {client.last_event_attended}
                  </td>
                  <td className="py-4 text-center text-slate-400">
                    {client.tickets_purchased}
                  </td>
                  <td className="py-4 text-center text-slate-400">
                    <Badge value={client.app_user} />
                  </td>

                  <td className="py-4 text-center text-slate-400">
                    <Badge value={client.promo_consent} />
                  </td>

                  <td className="py-4 text-center text-slate-400">
                    {formatDate(client.created_at)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="py-6 text-center text-slate-400">
                  No clients match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="flex justify-end">
          <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
            {/* ── Rows per page — always visible ── */}
            <span className="text-slate-400">Rows per page:</span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
            >
              {[5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            {totalPages > 1 && (
              <>
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
                >
                  Previous
                </button>

                {/* Pages */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber, index) =>
                    pageNumber === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-1 text-slate-500 select-none"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === pageNumber
              ? "bg-pink-600 text-white"
              : "text-slate-300 hover:bg-slate-800 hover:text-white"
          }`}
                      >
                        {pageNumber}
                      </button>
                    ),
                  )}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ImportClientsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          fetchClients();
          setToast({
            type: "success",
            message: "Clients imported successfully",
          });
        }}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>

    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#131C31] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const Badge = ({ value }) => {
  const isYes = value === true || value === "Yes";

  return (
    <span
      className={`px-2 py-1 text-xs rounded-full
        ${
          isYes
            ? "bg-green-500/10 text-green-400"
            : "bg-slate-700 text-slate-300"
        }`}
    >
      {isYes ? "Yes" : "No"}
    </span>
  );
};

export default ClientsPage;
